import "./assets/css/bootstrap-grid.min.css";
import './assets/scss/index.scss';
import 'i18n';

import React, { useEffect, Suspense, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import AuthService from 'services/auth/auth.service';
import ApiService from 'services/api/api.service';
import DeviceDetect from 'helpers/DeviceDetect';

import {
    STATIC_ROUTES_CONFIG,
    generateAllRoutesConfig
} from 'routing';

import {
    LANGUAGES,
    DEFAULT_LANG,
    detectLanguage,
    saveLanguageLocally,
    getLanguagePath,
    baseRouteUrl
} from 'helpers/util';

import {
    setRoutes,
    setMenus,
    setCurrency,
    setSocials,
    setContacts,
    setReady,
    setMobile,
    setLang
} from 'state/actions';

import ErrorBoundary from 'components/ErrorBoundary';
import AppLoader from 'components/elements/AppLoader';
import PageNotFound from 'components/pages/PageNotFound';
import Header from 'components/parts/Header';
import Footer from 'components/parts/Footer';
import SimplePopupWidget from 'components/widgets/Popups/SimplePopupWidget';
import { HreflangTags } from './HreflangTags';

import { loadUsetifulScript, setUsetifulTags } from 'usetiful-sdk';
import Cookies from 'universal-cookie';

const cookies = new Cookies();
const USETIFUL_TOKEN = 'e62313e4bf98d00633be563666466157';

function App() {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();

    const user = useSelector(store => store.user);
    const auth = useSelector(store => store.auth);
    const ready = useSelector(store => store.ready);
    const routes = useSelector(store => store.routes);

    const { isMobile } = DeviceDetect();

    const initializeUsetiful = (userId) => {
        if (!userId || typeof window === 'undefined') return;
        if (!window.USETIFUL) loadUsetifulScript(USETIFUL_TOKEN);

        const browserLang =
            window.navigator?.language?.split('-')[0]?.toLowerCase() || DEFAULT_LANG;

        setUsetifulTags({
            userId,
            language: browserLang
        });
    };

    /* ---------------------------------- */
    /* AUTH + ROUTES INIT                 */
    /* ---------------------------------- */
    const checkAuth = async () => {
        try {
            await AuthService.checkAuth();
        } finally {
            dispatch(setReady(true));
        }
    };

    const getRoutes = async () => {
        try {
            const res = await ApiService.getData("routes");
            dispatch(setCurrency(res.data.currency));
            dispatch(setRoutes(res.data.routes));
            dispatch(setMenus(res.data.menus));
            dispatch(setSocials(res.data.socials));
            dispatch(setContacts(res.data.contacts));
        } catch {
            dispatch(setCurrency({ symbol: "€", name: "eur" }));
            dispatch(setRoutes([]));
            dispatch(setMenus([]));
            dispatch(setSocials([]));
            dispatch(setContacts([]));
        }
    };

    /* ---------------------------------- */
    /* ROUTE CONFIG (memoized)            */
    /* ---------------------------------- */
    const ALL_ROUTES_CONFIG = useMemo(
        () =>
            generateAllRoutesConfig(
                STATIC_ROUTES_CONFIG,
                routes,
                auth,
                user,
                baseRouteUrl
            ),
        [routes, auth, user]
    );

    const renderLoader = () => <AppLoader />;

    const renderRoutesForLocale = (locale, routesList) => {
        return routesList.map(route => {
            const localizedPath = getLanguagePath(locale, route.path);

            return (
                <Route
                    key={`${locale}-${route.name}`}
                    path={localizedPath}
                    element={
                        <ErrorBoundary>
                            <Suspense fallback={renderLoader()}>
                                <route.Component {...route.props} />
                            </Suspense>
                        </ErrorBoundary>
                    }
                />
            );
        });
    };

    /* ---------------------------------- */
    /* EFFECTS                            */
    /* ---------------------------------- */

    // Mobile detection
    useEffect(() => {
        dispatch(setMobile(isMobile));
    }, [isMobile]);


    // Initial bootstrap
    useEffect(() => {


        const cookieLang = cookies.get('lang');
        const pathLang = detectLanguage();

        const targetLang =
            cookieLang && LANGUAGES.includes(cookieLang)
                ? cookieLang
                : pathLang || DEFAULT_LANG;

        saveLanguageLocally(targetLang);

        if (i18n.language !== targetLang) {
            i18n.changeLanguage(targetLang);
        }

        dispatch(setLang(targetLang));

        const correctPath = getLanguagePath(
            targetLang,
            window.location.pathname
        );

        if (window.location.pathname !== correctPath) {
            setTimeout(() => {
                window.location.replace(correctPath + window.location.search);
            });
            return;
        }



        if (AuthService.checkSession()) {
            checkAuth();
        } else {
            dispatch(setReady(true));
        }

        getRoutes();

        if (auth && user?.id) {
            ApiService.getUserData().then(userData => {
                if (userData?.id) {
                    initializeUsetiful(userData.id);
                }
            });
        }

    }, []);

    /* ---------------------------------- */
    /* RENDER                             */
    /* ---------------------------------- */

    return (
        <HelmetProvider>
            <div id="app" className={`App ${auth ? 'skin_green' : ''}`}>
                {ready && (
                    <>
                        <HreflangTags />
                        <SimplePopupWidget />

                        <ErrorBoundary>
                            <Suspense fallback={renderLoader()}>
                                <Header />
                            </Suspense>
                        </ErrorBoundary>

                        <Routes>
                            {LANGUAGES.map(locale =>
                                renderRoutesForLocale(locale, ALL_ROUTES_CONFIG)
                            )}
                            <Route path="*" element={<PageNotFound />} />
                        </Routes>

                        <ErrorBoundary>
                            <Suspense fallback={renderLoader()}>
                                <Footer />
                            </Suspense>
                        </ErrorBoundary>
                    </>
                )}
            </div>
        </HelmetProvider>
    );
}

export default App;